import { useEffect, useState } from 'react';
import { PageHeader } from '../components/common/PageHeader';
import { Check, Copy, PenLine, Search } from 'lucide-react';
import { AppLayout } from '../components/Layout/AppLayout';
import { CertificateSignModal } from '../components/Certificates/CertificateSignModal';
import { LoadingState, EmptyState, ErrorState } from '../components/common/FeedbackStates';
import { useAuth } from '../hooks/useAuth';
import { useCertificates } from '../hooks/useCertificates';
import { useSignatures } from '../hooks/useSignatures';
import { RECURSO_CERTIFICADOS, temRecurso } from '../utils/features';
import { certificadosParaAssinar, formatarDataHora, formatarTamanho, type SignatureEntry } from '../utils/signatureHistory';

const ESPERA_DA_BUSCA_MS = 300;

/**
 * Etapa 24: aba "Assinaturas". Assinar um PDF (download na hora) e ver o histórico.
 * O Lictor não guarda os PDFs: o histórico mostra só os dados de cada assinatura.
 */
export function Signatures() {
  const { membership } = useAuth();
  const liberado = temRecurso(membership, RECURSO_CERTIFICADOS);
  const { certificates, fetchCertificates, signPdf } = useCertificates();
  const { items, total, isLoading, error, fetchSignatures } = useSignatures();

  const [isSignOpen, setIsSignOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [busca, setBusca] = useState('');

  // Espera a pessoa parar de digitar antes de buscar no servidor.
  useEffect(() => {
    const espera = setTimeout(() => setBusca(search), ESPERA_DA_BUSCA_MS);
    return () => clearTimeout(espera);
  }, [search]);

  useEffect(() => {
    if (liberado) fetchCertificates();
  }, [liberado, fetchCertificates]);

  useEffect(() => {
    if (liberado) fetchSignatures(busca);
  }, [liberado, busca, fetchSignatures]);

  if (!liberado) {
    return (
      <AppLayout>
        <div style={{ color: 'var(--color-text-secondary)' }}>Este recurso não está disponível para a sua organização.</div>
      </AppLayout>
    );
  }

  const disponiveis = certificadosParaAssinar(certificates);
  const assinar = async (dados: FormData, certificateId: string) => {
    const assinado = await signPdf(certificateId, dados);
    fetchSignatures(busca);
    return assinado;
  };

  const buscando = busca.trim() !== '';
  const carregandoDoZero = isLoading && items.length === 0;

  return (
    <AppLayout>
      <PageHeader
        title="Assinaturas"
        description="Histórico dos PDFs assinados pelo Lictor. Os arquivos não ficam guardados: o PDF assinado é baixado na hora."
        actions={
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => setIsSignOpen(true)}
            disabled={disponiveis.length === 0}
            title={disponiveis.length === 0 ? 'Nenhum certificado disponível para você assinar' : undefined}
          >
            <PenLine size={16} /> Assinar PDF
          </button>
        }
      />

      <div style={{ position: 'relative', marginBottom: 'var(--space-6)', maxWidth: '420px' }}>
        <Search size={18} style={{ position: 'absolute', left: 'var(--space-3)', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }} />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar pelo nome do arquivo"
          aria-label="Buscar pelo nome do arquivo"
          style={{ width: '100%', padding: 'var(--space-3) var(--space-3) var(--space-3) var(--space-10)', background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)' }}
        />
      </div>

      {error ? (
        <ErrorState message={error} onRetry={() => fetchSignatures(busca)} />
      ) : carregandoDoZero ? (
        <LoadingState message="Carregando assinaturas..." />
      ) : items.length === 0 ? (
        <EmptyState
          title={buscando ? 'Nenhuma assinatura encontrada' : 'Nenhuma assinatura ainda'}
          description={buscando ? 'Nenhum arquivo assinado tem esse nome.' : 'Os PDFs assinados pelo Lictor aparecem aqui.'}
        />
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))', gap: 'var(--space-4)' }}>
            {items.map((item) => <CartaoDaAssinatura key={item.id} item={item} />)}
          </div>
          {items.length < total && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'var(--space-6)' }}>
              <button
                onClick={() => fetchSignatures(busca, true, items.length)}
                disabled={isLoading}
                style={{ padding: 'var(--space-2) var(--space-6)', background: 'transparent', border: '1px solid var(--color-border-default)', color: 'var(--color-text-secondary)', borderRadius: 'var(--radius-md)', cursor: isLoading ? 'wait' : 'pointer' }}
              >
                {isLoading ? 'Carregando...' : `Carregar mais (${total - items.length})`}
              </button>
            </div>
          )}
        </>
      )}

      {isSignOpen && (
        <CertificateSignModal
          certificados={disponiveis}
          onClose={() => setIsSignOpen(false)}
          onSign={assinar}
        />
      )}
    </AppLayout>
  );
}

function CartaoDaAssinatura({ item }: { item: SignatureEntry }) {
  const [copiado, setCopiado] = useState(false);
  const tamanho = formatarTamanho(item.file_size);
  const certificado = [item.certificate_label, item.holder && item.holder !== item.certificate_label ? item.holder : null]
    .filter(Boolean)
    .join(' · ');

  const copiarResumo = async () => {
    if (!item.sha256_signed) return;
    try {
      await navigator.clipboard.writeText(item.sha256_signed);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      // Sem permissão de área de transferência: o resumo continua visível no title.
    }
  };

  return (
    <div className="glass" style={{ padding: 'var(--space-5)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border-default)', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
        <div style={{ background: 'var(--color-bg-elevated)', padding: 'var(--space-2)', borderRadius: 'var(--radius-md)', flexShrink: 0 }}>
          <PenLine size={18} color="var(--color-primary-400)" />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 'var(--font-medium)', color: 'var(--color-text-primary)', overflowWrap: 'anywhere' }}>
            {item.file_name || 'Arquivo sem nome'}
          </div>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-1)' }}>
            {formatarDataHora(item.signed_at)}{tamanho ? ` · ${tamanho}` : ''}
          </div>
        </div>
      </div>

      <dl style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: 'var(--space-1) var(--space-3)', fontSize: 'var(--text-sm)', margin: 0 }}>
        <dt style={{ color: 'var(--color-text-secondary)' }}>Assinado por</dt>
        <dd style={{ margin: 0, overflowWrap: 'anywhere' }}>{item.signed_by_name || '—'}</dd>
        <dt style={{ color: 'var(--color-text-secondary)' }}>Certificado</dt>
        <dd style={{ margin: 0, overflowWrap: 'anywhere' }}>{certificado || '—'}</dd>
        {item.purpose && (
          <>
            <dt style={{ color: 'var(--color-text-secondary)' }}>Finalidade</dt>
            <dd style={{ margin: 0, overflowWrap: 'anywhere' }}>{item.purpose}</dd>
          </>
        )}
      </dl>

      {item.sha256_signed && (
        <button
          type="button"
          onClick={copiarResumo}
          title={`Resumo SHA-256 do PDF assinado: ${item.sha256_signed}`}
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0, padding: 'var(--space-2) var(--space-3)', background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-secondary)', fontSize: 'var(--text-xs)', cursor: 'pointer', textAlign: 'left' }}
        >
          {copiado ? <Check size={14} style={{ flexShrink: 0 }} /> : <Copy size={14} style={{ flexShrink: 0 }} />}
          <span style={{ flexShrink: 0 }}>{copiado ? 'Resumo copiado' : 'Resumo SHA-256'}</span>
          <code style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.sha256_signed}</code>
        </button>
      )}
    </div>
  );
}
